import { useMemo } from 'react';
import useEnums from './useEnum';
import useAuth from './useAuth';
import useSkipCompanyQuery from './useSkipCompanyQuery';
import { useGetCompanyQuery } from 'store/reducers/api/company';
import { isMobile } from 'react-device-detect';

/**
 * This function fetches configs related to Dynamic Enums
 * @returns An Object contains Configs related to Dynamic Enums
 */
const useDynamicConfig = () => {
  const { SensorStatesEnum, SensorStatusesEnum, UnitCategoriesEnum, UserRolesEnum, NotificationStatusesEnum } = useEnums();
  const { activeRole } = useAuth();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany } = useGetCompanyQuery(activeRole?.company_id!, {
    skip: skipCompanyQuery
  });

  const DEFAULT_ROLE_BASED_PATH = useMemo(() => {
    const isBillingCompany = userCompany?.company_type_id === 3;
    return {
      [UserRolesEnum.SYSTEM_ADMIN]: '/companies',
      [UserRolesEnum.SYSTEM_SUPPORT]: '/companies',
      [UserRolesEnum.SYSTEM_TECHNICIAN]: '/companies',
      [UserRolesEnum.COMPANY_ADMIN]: isBillingCompany ? '/companies' : '/dashboard',
      [UserRolesEnum.COMPANY_MANAGER]: isBillingCompany ? '/companies' : '/dashboard',
      [UserRolesEnum.SENSOR_TESTER]: '/micrometers/testing',
      [UserRolesEnum.COMPANY_INSTALLER]: '/installation',
      [UserRolesEnum.PROPERTY_OPERATOR]: '/dashboard',
      [UserRolesEnum.NOTIFICATION_CONTACT]: '/settings'
    };
  }, [UserRolesEnum, userCompany]);

  const DEFAULT_MM_FILTER = useMemo(
    () => ({
      unitCategoryId: [UnitCategoriesEnum.INTERNAL_UNITS].map(String),
      'filter.sensor_state_id': `$in:${[SensorStatesEnum.INSTALLED].join(',')}`,
      ...(!isMobile &&
        !(activeRole && activeRole.user_role_type_id > UserRolesEnum.SYSTEM_SUPPORT) && {
          'filter.sensor_status_id': `$in:${[SensorStatusesEnum.TRANSMITTING].join(',')}`
        })
    }),
    [UnitCategoriesEnum, SensorStatesEnum, SensorStatusesEnum, UserRolesEnum, activeRole]
  );

  const DEFAULT_ALERT_FILTER = useMemo(
    () => ({
      status: [NotificationStatusesEnum.OPEN, NotificationStatusesEnum.ACKNOWLEDGED].map(String)
    }),
    [NotificationStatusesEnum]
  );

  const SYSTEM_ROLES = useMemo(
    () => [UserRolesEnum.SYSTEM_ADMIN, UserRolesEnum.SYSTEM_SUPPORT, UserRolesEnum.SYSTEM_TECHNICIAN],
    [UserRolesEnum]
  );
  const COMPANY_ROLES = useMemo(() => [UserRolesEnum.COMPANY_ADMIN, UserRolesEnum.COMPANY_MANAGER], [UserRolesEnum]);
  const PROPERTY_ROLES = useMemo(() => [UserRolesEnum.PROPERTY_OPERATOR], [UserRolesEnum]);

  return { DEFAULT_ROLE_BASED_PATH, DEFAULT_MM_FILTER, SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES, DEFAULT_ALERT_FILTER };
};

export default useDynamicConfig;
